import { apiService } from '@shared/services/apiServices';
import { AxiosError } from 'axios';
import contactRequestEndpoints from './contactRequestEndpoints';
import {
  handleContactRequestError,
  ContactRequestErrorCode,
  contactRequestErrorMessages,
} from '../errors';

// ─── Response shape ───────────────────────────────────────────────────────────

/** Single admin note entry returned inside the admin_notes array. */
export interface AdminNote {
  note: string;
  /** ISO-8601 string — format before display */
  created_at: string;
}

/** Full detail payload returned by GET contact-request/contact-requests/:id */
export interface ViewContactRequestData {
  /** Internal UUID — used as the route param */
  id: string;
  /** Human-readable sequential ID, e.g. SR2026000010 */
  contact_request_id: string;
  subject: string;
  /** Body of the contact request (max 500 chars) */
  message: string;
  /** Admin-only notes array; always an array (empty when no notes added yet) */
  admin_notes: AdminNote[];
  status: string;
  assigned_to: string | null;
  client_id: string | null;
  created_by: string | null;
  /** ISO-8601 string — format before display */
  created_at: string;
  updated_at: string;
  client_name: string | null;
  /** Sequential client ID, e.g. CLIENT000011 */
  client_user_id: string | null;
  client_email: string | null;
  /** Pre-formatted "countryCode phoneNumber" string assembled by the backend */
  client_phone: string | null;
  client_address_line1: string | null;
  client_address_line2: string | null;
  client_city: string | null;
  client_state: string | null;
  client_zip_code: string | null;
  client_country: string | null;
}

export interface ViewContactRequestResult {
  success: boolean;
  data?: ViewContactRequestData;
  error?: string;
}

// ─── API function ─────────────────────────────────────────────────────────────

/**
 * Fetches full detail of a single contact request by its UUID.
 *
 * Endpoint: `GET contact-request/contact-requests/{id}`
 *
 * Access: SUPER_ADMIN (any request) | ADMIN (tenant-scoped, returns 404 if outside tenant)
 *
 * @param id - UUID of the contact request (`ContactRequestListItem.id`)
 */
export const viewContactRequest = async (
  id: string
): Promise<ViewContactRequestResult> => {
  try {
    const result = await apiService.getAPI(
      `${contactRequestEndpoints.LIST_CONTACT_REQUESTS}/${id}`
    );

    if (!result.error) {
      return { success: true, data: result.data as ViewContactRequestData };
    }

    const { errorMessage } = handleContactRequestError(
      result.status as number | undefined,
      result.data,
      !!result.isNetworkError
    );
    return { success: false, error: errorMessage };
  } catch (error) {
    if (error instanceof AxiosError) {
      const { errorMessage } = handleContactRequestError(
        error.response?.status,
        error.response?.data,
        !error.response && !!error.request
      );
      return { success: false, error: errorMessage };
    }
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : contactRequestErrorMessages[
              ContactRequestErrorCode.UNEXPECTED_ERROR
            ],
    };
  }
};
